import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import '../../styles/LabTestReport.css';
import logo from '../../assets/logo192.png';
import { getFormattedDateTime } from '../../assets/Utils';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import EmailService from '../../services/emailService';
import { useAuth } from '../../hooks/useAuth';
import { labTestDisplayName } from '../../utils/labTestDisplayName';

const LabTestReport = ({ reportId: reportIdProp, onClose } = {}) => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const { id: stateId } = useLocation().state || {};
  const id = reportIdProp || stateId || searchParams.get('id');
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [emailStatus, setEmailStatus] = useState({
    sending: false,
    sent: false,
    error: null,
    patientHasEmail: true
  });

  const cbcUnitsMap = {
    hemoglobin: 'g/dL',
    'rbc – erythrocytes': 'x10⁶/uL',
    'hct – hematocrit': '%',
    mcv: 'fL',
    mch: 'pg',
    mchc: 'g/dL',
    rdwcv: '%',
    platelets: 'x10³/uL',
    mpv: 'fL',
    'pdw-cv': '%',
    'wbcs – leucocytes': 'x10³/uL',
    'neutrophil %': '%',
    'lymphocytes %': '%',
    'monocytes %': '%',
    'eosinophil %': '%',
    'basophil %': '%',
    'pct – plateletcrit': '%',
    'lymphocyte #': 'x10³/uL',
    'monocyte #': 'x10³/uL',
    'neutrophil #': 'x10³/uL',
    'eosinophil #': 'x10³/uL',
    'basophil #': 'x10³/uL',
    'wbc': '10^9/L',
    'lym#': '10^9/L',
    'mid#': '10^9/L',
    'gra#': '10^9/L',
    'rbc': '10^12/L',
    'hgb': 'g/L',
    'hct': '%',
    'mcv': 'fL',
    'mch': 'pg',
    'mchc': 'g/L',
    'rdw-cv': '%',
    'plt': '10^9/L',
    'mpv': 'fL',
    'pdw': '%',
    'pct': '%',
    'lym%': '%',
    'mid%': '%',
    'gra%': '%',
    'p-lcr': '%',
    'p-lcc': '10^9/L',
    'rdw-sd': 'fL',
    'band neutrophils %': '%',
    'band neutrophils #': 'x10³/uL',
    'total neutrophils %': '%',
    'total neutrophils #': 'x10³/uL',
    'ig%': '%',
    'ig#': 'x10³/uL',
  };

  // CBC grouping configuration — standard clinical panel order
  const CBC_RBC_TESTS = [
    'Hemoglobin', 'HCT-Hematocrit', 'RBC-Erythrocytes',
    'MCV', 'MCH', 'MCHC', 'RDWcv', 'RDW-SD',
  ];
  const CBC_PLT_TESTS = [
    'Platelets', 'PCT-Plateletcrit', 'MPV', 'PDW-CV', 'P-LCC', 'P-LCR',
  ];
  const WBC_DIFF_PAIRS = [
    { label: 'Neutrophils (Seg)', pctKey: 'Neutrophil %', absKey: 'Neutrophil #' },
    { label: 'Band Neutrophils', pctKey: 'Band Neutrophils %', absKey: 'Band Neutrophils #' },
    { label: 'Total Neutrophils', pctKey: 'Total Neutrophils %', absKey: 'Total Neutrophils #' },
    { label: 'Lymphocytes', pctKey: 'Lymphocytes %', absKey: 'Lymphocyte #' },
    { label: 'Monocytes', pctKey: 'Monocytes %', absKey: 'Monocyte #' },
    { label: 'Eosinophils', pctKey: 'Eosinophil %', absKey: 'Eosinophil #' },
    { label: 'Basophils', pctKey: 'Basophil %', absKey: 'Basophil #' },
    { label: 'Immature Granulocytes', pctKey: 'IG%', absKey: 'IG#' },
  ];

  const groupCbcData = (data) => {
    if (!data || !Array.isArray(data)) return null;
    const lookup = new Map(data.map((r) => [r.testName, r]));

    const pickRows = (keys) =>
      keys.map((k) => lookup.get(k)).filter(Boolean);

    const rbc = pickRows(CBC_RBC_TESTS);
    const plt = pickRows(CBC_PLT_TESTS);

    // WBC total
    const wbcTotal = lookup.get('WBCs-Leucocytes') || null;

    // Determine band-neutrophil visibility
    const bandRow = lookup.get('Band Neutrophils %') || lookup.get('Band Neutrophils #');
    const hasBand = bandRow && parseFloat(bandRow.testValue) > 0;

    const diffRows = WBC_DIFF_PAIRS
      .filter(({ label }) => {
        if (!hasBand && (label === 'Band Neutrophils' || label === 'Total Neutrophils')) return false;
        return true;
      })
      .map(({ label, pctKey, absKey }) => {
        const pct = lookup.get(pctKey);
        const abs = lookup.get(absKey);
        if (!pct && !abs) return null;
        // When band is absent, relabel segmented as just "Neutrophils"
        const displayLabel = (!hasBand && label === 'Neutrophils (Seg)') ? 'Neutrophils' : label;
        return { label: displayLabel, pct, abs };
      })
      .filter(Boolean);

    return { rbc, plt, wbcTotal, diffRows };
  };

  // Helper to check range flag.
  const checkRangeFlag = (valueStr, rangeStr) => {
    if (!rangeStr || !valueStr) return '';
    const match = rangeStr.match(/([\d.]+)\s*-\s*([\d.]+)/);
    if (!match) return '';
    const minVal = parseFloat(match[1]);
    const maxVal = parseFloat(match[2]);
    const val = parseFloat(valueStr);
    if (isNaN(minVal) || isNaN(maxVal) || isNaN(val)) return '';
    if (val < minVal) return 'L';
    if (val > maxVal) return 'H';
    return '';
  };

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }
    const fetchReportDetails = async () => {
      try {
        console.log('Fetching report details for ID:', id);
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/lab/lab-request-details/${id}`
        );
        setReport(response.data);
      } catch (error) {
        console.error('Error fetching report details:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReportDetails();
  }, [id]);

  if (isLoading) {
    return (
      <div className="lab-test-report printable">
        {onClose && (
          <div className="action-buttons no-print">
            <button className="btn-close-report" onClick={onClose} aria-label="Close report">
              X
            </button>
          </div>
        )}
        <p>Loading report details...</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="lab-test-report printable">
        {onClose && (
          <div className="action-buttons no-print">
            <button className="btn-close-report" onClick={onClose} aria-label="Close report">
              X
            </button>
          </div>
        )}
        <p>No report data available.</p>
      </div>
    );
  }

  // Use report.request as header; inject patient if missing.
  const reportHeader = report.request || {};
  if (!reportHeader.Patient && report.patient) {
    reportHeader.Patient = report.patient;
  }
  const details = report.details || [];

  // Separate out the CBC detail ("Attach CBC File") from the other details.
  const cbcDetail = details.find(
    (detail) =>
      detail.MedicalLabTest.test_name.toLowerCase() === 'full blood count'
  );
  const nonCbcDetails = details.filter(
    (detail) =>
      detail.MedicalLabTest.test_name.toLowerCase() !== 'full blood count'
  );

  let cbcData = null;
  if (cbcDetail && cbcDetail.result) {
    try {
      cbcData = JSON.parse(cbcDetail.result);
    } catch (error) {
      console.error('Error parsing CBC result JSON:', error);
    }
  }

  const getUnit = (testName) => {
    const normalized = testName.toLowerCase().replace(/[^\w]+/g, '');
    let matchedKey = Object.keys(cbcUnitsMap).find(
      (k) => k.replace(/[^\w]+/g, '') === normalized
    );
    return matchedKey ? cbcUnitsMap[matchedKey] : '';
  };

  // Group non-CBC details by category and then by type.
  const groupedNonCbc = nonCbcDetails.reduce((acc, detail) => {
    const cat = detail.MedicalLabTest.category || 'Uncategorized';
    const typ = detail.MedicalLabTest.type || 'Other';
    if (!acc[cat]) acc[cat] = {};
    if (!acc[cat][typ]) acc[cat][typ] = [];
    acc[cat][typ].push(detail);
    return acc;
  }, {});

  // Action button handlers.
  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    // Temporarily hide action buttons
    const actionButtons = document.querySelector('.action-buttons');
    const originalDisplay = actionButtons ? actionButtons.style.display : '';
    if (actionButtons) {
      actionButtons.style.display = 'none';
    }

    try {
      // Create PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;

      // Function to add image with proper scaling
      const addImageToPDF = (canvas, yPosition = margin) => {
        const imgWidth = pageWidth - (2 * margin);
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        pdf.addImage(
          canvas.toDataURL('image/jpeg', 1.0),
          'JPEG',
          margin,
          yPosition,
          imgWidth,
          imgHeight,
          undefined,
          'FAST'
        );
        
        return yPosition + imgHeight; // Return the bottom position
      };

      // First capture and add the header
      const headerSection = document.querySelector('.report-header');
      const headerCanvas = await html2canvas(headerSection, {
        scale: 2,
        logging: false,
        useCORS: true
      });
      let currentY = addImageToPDF(headerCanvas);
      
      // Add title
      const titleSection = document.querySelector('.test-results > h2');
      if (titleSection) {
        const titleCanvas = await html2canvas(titleSection, {
          scale: 2,
          logging: false,
          useCORS: true
        });
        currentY = addImageToPDF(titleCanvas, currentY + 5);
      }

      // Capture and add main report sections
      const sections = document.querySelectorAll('.test-category-section');
      for (let section of sections) {
        const canvas = await html2canvas(section, {
          scale: 2,
          logging: false,
          useCORS: true
        });
        
        // Check if we need a new page
        const imgWidth = pageWidth - (2 * margin);
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        if (currentY + imgHeight > pageHeight - margin) {
          pdf.addPage();
          currentY = margin;
        }
        
        currentY = addImageToPDF(canvas, currentY + 5);
      }

      // Add CBC section if it exists
      const cbcSection = document.querySelector('.cbc-section');
      if (cbcSection) {
        pdf.addPage();
        const cbcCanvas = await html2canvas(cbcSection, {
          scale: 2,
          logging: false,
          useCORS: true
        });
        addImageToPDF(cbcCanvas);
      }

      pdf.save(`LabReport_${reportHeader.id}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      // Restore action buttons
      if (actionButtons) {
        actionButtons.style.display = originalDisplay;
      }
    }
  };

  const handleEmailPatient = async () => {
    if (!report || !reportHeader.Patient) {
      setEmailStatus(prev => ({ ...prev, error: 'Report or patient data not available' }));
      return;
    }

    setEmailStatus(prev => ({ 
      ...prev, 
      sending: true, 
      sent: false, 
      error: null 
    }));

    try {
      // Get the printable content
      const printableElement = document.querySelector('.lab-test-report.printable');
      if (!printableElement) {
        throw new Error('Report content not found');
      }

      // Clone the element to avoid modifying the original
      const clonedElement = printableElement.cloneNode(true);
      
      // Remove action buttons from the cloned element
      const actionButtons = clonedElement.querySelector('.action-buttons');
      if (actionButtons) {
        actionButtons.remove();
      }

      // Get the HTML content
      const reportHtml = clonedElement.outerHTML;

      // Initialize email service
      const emailService = new EmailService();

      // Prepare email data
      const emailData = {
        reportId: reportHeader.id || 'N/A',
        patientId: reportHeader.Patient.patient_id,
        reportHtml: reportHtml,
        reportType: reportHeader.request_type || 'Lab Report'
      };

      // Send email
      const result = await emailService.sendLabReport(emailData, token);

      if (result.success) {
        setEmailStatus(prev => ({
          ...prev,
          sending: false,
          sent: true,
          error: null,
          jobId: result.data.jobId
        }));

        // Show success message for a few seconds
        setTimeout(() => {
          setEmailStatus(prev => ({ ...prev, sent: false }));
        }, 5000);
      } else {
        setEmailStatus(prev => ({
          ...prev,
          sending: false,
          sent: false,
          error: result.error,
          patientHasEmail: result.details?.patientHasEmail !== false
        }));
      }

    } catch (error) {
      console.error('Error sending email:', error);
      setEmailStatus(prev => ({
        ...prev,
        sending: false,
        sent: false,
        error: error.message
      }));
    }
  };

  return (
    <div className="lab-test-report printable">
      {/* Action Buttons: hidden on print */}
      <div className="action-buttons no-print">
        {onClose && (
          <button className="btn-close-report" onClick={onClose} aria-label="Close report">
            X
          </button>
        )}
        <button className="btn-print" onClick={handlePrint}>
          Print Report
        </button>
        <button className="btn-download" onClick={handleDownloadPDF}>
          Download PDF
        </button>
        <button 
          className={`btn-email ${emailStatus.sending ? 'sending' : ''} ${emailStatus.sent ? 'sent' : ''}`}
          onClick={handleEmailPatient}
          disabled={emailStatus.sending}
        >
          {emailStatus.sending ? 'Sending...' : emailStatus.sent ? 'Email Sent!' : 'Email Patient'}
        </button>
        
        {emailStatus.error && (
          <div className="email-error">
            <span>❌ {emailStatus.error}</span>
            {!emailStatus.patientHasEmail && (
              <span className="email-help">
                (Patient email not available in records)
              </span>
            )}
          </div>
        )}
        
        {emailStatus.sent && (
          <div className="email-success">
            <span>✅ Email sent successfully!</span>
          </div>
        )}
      </div>

      <header className="report-header">
        <div className="lab-logo">
          <img src={logo} alt="Lab Logo" />
        </div>
        <div className="header-compact">
          <div className="lab-info">
            <h1>Shuman Hospital Laboratory</h1>
            <p>15/17 Pultney Street, Cotton Tree</p>
            <p>Phone: (+232) 090 33 00 00</p>
          </div>
          <div className="patient-compact">
            <p>
              <strong>ID:</strong> {reportHeader.Patient?.patient_id || 'N/A'}
            </p>
            <p>
              <strong>Name:</strong>{' '}
              {reportHeader.Patient
                ? `${reportHeader.Patient.patient_first_name} ${reportHeader.Patient.patient_last_name}`
                : 'N/A'}
            </p>
            <p>
              <strong>DOB:</strong> {reportHeader.Patient?.patient_dob || 'N/A'}
            </p>
            <p>
              <strong>Company:</strong> {reportHeader.Patient?.company || 'N/A'}
            </p>
          </div>
          <div className="report-meta">
            <p>
              <strong>Report ID:</strong> {reportHeader.id || 'N/A'}
            </p>
            <p>
              <strong>Date:</strong> {getFormattedDateTime(reportHeader.requestDate) || 'N/A'}
            </p>
          </div>
        </div>
      </header>

      <section className="test-results">
        <h2>{reportHeader.request_type ? reportHeader.request_type : 'Lab'} Report</h2>
        {Object.entries(groupedNonCbc).map(([category, typesObj]) => (
          <div key={category} className="test-category-section">
            <h3 className="category-header">{category}</h3>
            {Object.entries(typesObj).map(([type, detailsArr]) => (
              <div key={type} className="test-type-section">
                <h4 className="type-header">{type}</h4>
                {reportHeader.request_type && reportHeader.request_type.toLowerCase() === 'radiology' ? (
                  <table className="results-table">
                    <thead>
                      <tr>
                        <th>Test</th>
                        <th>Result</th>
                        <th>Indications</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailsArr.map((detail) => {
                        const testName = labTestDisplayName(detail.MedicalLabTest.test_name, detail.side);
                        const testResult = detail.result;
                        const indications = detail.result_note || '';
                        return (
                          <tr key={detail.id}>
                            <td>{testName}</td>
                            <td>{testResult}</td>
                            <td>{indications}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <table className="results-table">
                    <thead>
                      <tr>
                        <th>Test</th>
                        <th>Result</th>
                        <th>Units</th>
                        <th>Reference Range</th>
                        <th>Indicator</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailsArr.map((detail) => {
                        const testName = labTestDisplayName(detail.MedicalLabTest.test_name, detail.side);
                        const testResult = detail.result;
                        const unit = detail.MedicalLabTest.test_unit || '';
                        const refRange = detail.MedicalLabTest.test_reference_range || 'N/A';
                        const flag = checkRangeFlag(testResult, refRange);
                        let flagStyle = {};
                        if (flag === 'L') flagStyle = { color: 'blue' };
                        if (flag === 'H') flagStyle = { color: 'red' };
                        return (
                          <tr key={detail.id}>
                            <td>{testName}</td>
                            <td className="multiline">{testResult}</td>
                            <td>{unit}</td>
                            <td>{refRange}</td>
                            <td style={flagStyle}>{flag}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        ))}

        {cbcData && (() => {
          const grouped = groupCbcData(cbcData);
          if (!grouped) return null;
          const { rbc, plt, wbcTotal, diffRows } = grouped;

          const renderStandardRow = (row, idx) => {
            const unit = getUnit(row.testName);
            const flag = checkRangeFlag(row.testValue, row.range);
            let flagStyle = {};
            if (flag === 'L') flagStyle = { color: 'blue', fontWeight: 'bold' };
            if (flag === 'H') flagStyle = { color: 'red', fontWeight: 'bold' };
            return (
              <tr key={row.testName + idx}>
                <td>{row.testName}</td>
                <td style={flagStyle}>{row.testValue}</td>
                <td>{unit}</td>
                <td>{row.range}</td>
                <td style={flagStyle}>{flag}</td>
              </tr>
            );
          };

          const renderGroupHeader = (title) => (
            <tr key={`group-${title}`} className="cbc-group-header">
              <td colSpan="5">{title}</td>
            </tr>
          );

          return (
            <section className="cbc-section">
              <h2>Complete Blood Count</h2>
              <table className="results-table cbc-table">
                <thead>
                  <tr>
                    <th>Test</th>
                    <th>Value</th>
                    <th>Units</th>
                    <th>Range</th>
                    <th>Ind.</th>
                  </tr>
                </thead>
                <tbody>
                  {/* --- Red Blood Cells --- */}
                  {rbc.length > 0 && renderGroupHeader('Red Blood Cells')}
                  {rbc.map((row, idx) => renderStandardRow(row, idx))}

                  {/* --- White Blood Cells --- */}
                  {(wbcTotal || diffRows.length > 0) && renderGroupHeader('White Blood Cells')}
                  {wbcTotal && renderStandardRow(wbcTotal, 0)}

                  {diffRows.length > 0 && (
                    <>
                      <tr className="cbc-sub-header">
                        <td>Differential</td>
                        <td>%</td>
                        <td>% Range</td>
                        <td>#&nbsp;&nbsp;&nbsp;&nbsp;(Unit)</td>
                        <td># Range</td>
                      </tr>
                      {diffRows.map(({ label, pct, abs }) => {
                        const pctFlag = pct ? checkRangeFlag(pct.testValue, pct.range) : '';
                        const absFlag = abs ? checkRangeFlag(abs.testValue, abs.range) : '';
                        const flagStyle = (f) => {
                          if (f === 'L') return { color: 'blue', fontWeight: 'bold' };
                          if (f === 'H') return { color: 'red', fontWeight: 'bold' };
                          return {};
                        };
                        const absUnit = abs ? getUnit(abs.testName) : '';
                        return (
                          <tr key={label} className="cbc-diff-row">
                            <td>{label}</td>
                            <td style={flagStyle(pctFlag)}>
                              {pct ? pct.testValue : '-'}
                              {pctFlag && <span className="cbc-flag"> {pctFlag}</span>}
                            </td>
                            <td>{pct ? pct.range : '-'}</td>
                            <td style={flagStyle(absFlag)}>
                              {abs ? abs.testValue : '-'}
                              {absUnit && <span className="cbc-diff-unit"> {absUnit}</span>}
                              {absFlag && <span className="cbc-flag"> {absFlag}</span>}
                            </td>
                            <td>{abs ? abs.range : '-'}</td>
                          </tr>
                        );
                      })}
                    </>
                  )}

                  {/* --- Platelets --- */}
                  {plt.length > 0 && renderGroupHeader('Platelets')}
                  {plt.map((row, idx) => renderStandardRow(row, idx))}
                </tbody>
              </table>
            </section>
          );
        })()}
      </section>

      {/* Findings Section - only for Radiology */}
      {reportHeader.request_type &&
        reportHeader.request_type.toLowerCase() === 'radiology' &&
        reportHeader.findings && (
          <section className="findings-section">
            <h3>Findings</h3>
            <p>{reportHeader.findings}</p>
          </section>
        )}

      <footer className="report-footer">
        <p>
          This document is intended for informational purposes only and should
          be interpreted by a qualified healthcare professional.
        </p>
        <p className="signature-line"></p>
      </footer>
    </div>
  );
};

export default LabTestReport;
